import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Col, Dropdown, Form, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Overlay from 'react-bootstrap/Overlay';
import Popover from 'react-bootstrap/Popover';
import TextareaAutosize from 'react-textarea-autosize';
import {Button} from '@/components/ui';
import axios from 'axios';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {buildSearchDashboardURL} from '@/utils/urlBuilder';
import ChartQuerySyntaxFeedback from '@/views/storyboard/chart_settings/ChartQuerySyntaxFeedback';
import {formatLuceneQuery} from '@/views/storyboard/chart_settings/formatLuceneQuery';
import {serialize, validateQueryString} from '@/views/storyboard/utils/helpers';
import {validateLuceneQuerySyntax} from '@/views/storyboard/utils/validateLuceneQuerySyntax';
import {useStoreActions, useStoreState} from 'store';
import {FormikValues} from 'formik/dist/types';
import {navigate} from '@reach/router';
import i18n from 'i18n';
import {FormikT} from 'utils/types';
import {SearchType} from 'entities/search';
import Icon from '@/components/icons/Icon';
import {hasPermission} from '@/utils/permissions/permissionDebug';


export enum SearchBoxType {
    Search = 'search',
    Chart = 'chart',
    Header = 'header',
}

type SearchBoxElement = HTMLInputElement | HTMLTextAreaElement;

interface BaseProps {
    controlId?: string;
    className?: string;
    style?: CSSProperties;
    deferHelpLink?: boolean;
}

interface PropsWithoutFormik {
    type: SearchBoxType.Header | SearchBoxType.Search;
    formik?: never;
}

interface PropsWithFormik {
    type: SearchBoxType.Chart;
    formik: FormikT<any>;
}

export const ChartSearchQueryHelpLink: React.FC = () => {
    const setSamplesOpen = useStoreActions((actions) => actions.queryHelpModal.setQueryHelpModalOpen);

    return (
        <div className="chart-settings-form-search-help">
            <Tooltip
                content={i18n.t('tt_how_write.text')}
                placement="top"
            >
                <Button
                    variant="link"
                    type="button"
                    onClick={() => setSamplesOpen(true)}
                    style={{padding: '0'}}
                    className="text-info"
                >
                    <span className="d-inline-flex align-items-center">
                        <Icon name="mdi-open-in-new" size="1em" className="mr-1" />
                        {i18n.t('how_to_write_queries.text')}
                    </span>
                </Button>
            </Tooltip>
        </div>
    );
};

type SearchBoxProps = BaseProps & (PropsWithFormik | PropsWithoutFormik);

type SearchBoxInnerProps = SearchBoxProps & {
    userData: any;
};

/**
 * Inner implementation of SearchBox that assumes userData is already loaded.
 * This keeps all hooks in a stable order and avoids early returns inside
 * the hook-using component.
 */
function SearchBoxInner(
    {
        type = SearchBoxType.Search,
        controlId,
        className,
        style,
        formik,
        userData,
        deferHelpLink = false,
    }: SearchBoxInnerProps
) {

    const currentRef = useRef<HTMLDivElement>(null);

    const [show, setShow] = useState<boolean>(false);

    const currentTarget = useRef<SearchBoxElement>(null);

    const setSamplesOpen = useStoreActions((actions) => actions.queryHelpModal.setQueryHelpModalOpen);

    const handleOpenModal = () => {
        setSamplesOpen(true);
    };

    const activeWord = useStoreState((state) => state.search.activeWord);
    const storeActiveWord = useStoreActions((state) => state.search.setActiveWord);

    const similarWords = useStoreState((state) => state.search.similarWords);
    const storeSimilarWords = useStoreActions((state) => state.search.setSimilarWords);

    const loadingSimilarWords = useStoreState((state) => state.search.loadingSimilarWords);
    const storeLoadingSimilarWords = useStoreActions((state) => state.search.setLoadingSimilarWords);

    const headerActiveWord = useStoreState((state) => state.search.headerActiveWord);
    const storeHeaderActiveWord = useStoreActions((state) => state.search.setHeaderActiveWord);

    const headerSimilarWords = useStoreState((state) => state.search.headerSimilarWords);
    const storeHeaderSimilarWords = useStoreActions((state) => state.search.setHeaderSimilarWords);

    const headerSearchQuery = useStoreState((state) => state.search.headerSearchQuery);
    const storeHeaderSearchQuery = useStoreActions((state) => state.search.setHeaderSearchQuery);

    const loadingHeaderSimilarWords = useStoreState((state) => state.search.loadingHeaderSimilarWords);
    const storeLoadingHeaderSimilarWords = useStoreActions((state) => state.search.setLoadingHeaderSimilarWords);

    const searchQuery = useStoreState((state) => state.search.searchQuery);
    const storeSearchQuery = useStoreActions((state) => state.search.setSearchQuery);

    const viewTypeQuery = useStoreState((state) => state.search.viewTypeQuery);
    const storeViewTypeQuery = useStoreActions((state) => state.search.setViewTypeQuery);

    const errors = useStoreState((state) => state.search.errors);
    const storeErrors = useStoreActions((actions) => actions.search.setErrors);

    // When another SearchBox opens its similar-words popover, our active word is cleared
    useEffect(() => {
        const ours = type === SearchBoxType.Header ? headerActiveWord : activeWord;
        if (ours === null) setShow(false);
    }, [type, headerActiveWord, activeWord]);

    const [viewTypeFieldChoices] = useData<[string, string][]>(window.URLS.viewTypeFieldChoices,
        'viewTypeFieldChoices', 'loadViewTypeFieldChoices');

    const viewTypeI18nKeyByValue: Record<string, string> = {
        default: 'jt_view_type_default.text',
    };

    const [searchLengthWarning, setSearchLengthWarning] = useState<string | null>(null);
    const appConfig = useStoreState((state) => state.appConfigs.items);

    const userCanAccessViewTypeFieldChoices = hasPermission('fs.application.settings.read', userData?.permissions, true);

    let currentActiveWord: null | string;
    let currentSearchQuery: string;
    let currentErrors: undefined | string;
    if (type === SearchBoxType.Search) {
        currentActiveWord = activeWord;
        currentSearchQuery = searchQuery;
        currentErrors = errors.query_string;
    } else if (type === SearchBoxType.Header) {
        currentActiveWord = headerActiveWord;
        currentSearchQuery = headerSearchQuery;
        currentErrors = undefined;
    } else {
        currentActiveWord = activeWord;
        currentSearchQuery = formik!.values.query_string;
        currentErrors = formik!.errors.query_string as string;
    }

    let currentSimilarWords;
    let loadingCurrentSimilarWords;
    if (type === SearchBoxType.Header) {
        currentSimilarWords = headerSimilarWords;
        loadingCurrentSimilarWords = loadingHeaderSimilarWords;
    } else {
        currentSimilarWords = similarWords;
        loadingCurrentSimilarWords = loadingSimilarWords;
    }

    const getSuggestions = (value: string): void => {
        const fetchData = async () => {
            if (type === SearchBoxType.Header) {
                storeLoadingHeaderSimilarWords(true);
            } else {
                storeLoadingSimilarWords(true);
            }
            let response;
            if (value) {
                try {
                    response = await axios.get(window.URLS.word2VecSimilarWords, {
                        headers: {'Content-Type': 'application/json'},
                        params: {
                            query: decodeURIComponent(value),
                        },
                    });
                    if (type === SearchBoxType.Header) {
                        storeHeaderSimilarWords(response.data);
                        storeLoadingHeaderSimilarWords(false);
                    } else {
                        storeSimilarWords(response.data);
                        storeLoadingSimilarWords(false);
                    }
                } catch (e) {
                    if (type === SearchBoxType.Header) {
                        storeHeaderSimilarWords([]);
                        storeLoadingHeaderSimilarWords(false);
                    } else {
                        storeSimilarWords([]);
                        storeLoadingSimilarWords(false);
                    }
                }
            }
        };
        fetchData();
    };

    const onInputClick = (event: React.MouseEvent<SearchBoxElement, MouseEvent>) => {
        const excludeList = ['AND', 'OR'];
        const inputElement = event.target as SearchBoxElement;
        const inputValue = inputElement.value;
        if (inputValue) {
            let string = `${inputValue} `;

            // Replace new lines with spaces
            string = string.replace(/\n/g, ' ');

            // Slice the string from the beginning to the first space position
            const slicedString = string.slice(0, string.indexOf(' ', inputElement.selectionEnd ?? 0));
            let result = /\S+$/g.exec(slicedString)?.[0] ?? '';

            // If the user has selected a word, use that instead
            if (inputElement.selectionStart !== inputElement.selectionEnd) {
                result = string.slice(inputElement.selectionStart ?? 0, inputElement.selectionEnd ?? 0);
            }

            // Remove any trailing ~
            if (result.indexOf('~') > -1) {
                result = result.substring(0, result.indexOf('~'));
            }

            // Remove any special characters
            result = result.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');

            // Remove any trailing -
            result = result.replace('-', '');

            if (!excludeList.includes(result) && (currentActiveWord !== result)) {
                // Close the other SearchBox's similar-words popover so only one is open
                if (type === SearchBoxType.Header) {
                    storeActiveWord(null);
                    storeSimilarWords([]);
                    storeLoadingSimilarWords(false);
                    storeHeaderActiveWord(result);
                } else {
                    storeHeaderActiveWord(null);
                    storeHeaderSimilarWords([]);
                    storeLoadingHeaderSimilarWords(false);
                    storeActiveWord(result);
                }
                getSuggestions(result);
            }
        }
        setTimeout(setShow, 100, true);
    };

    const onHide = (event: Event): void => {
        // Not sure how to tell TS that event.target MIGHT have a value
        // @ts-ignore
        if (!event.target?.value) {
            setShow(false);
        }
    };

    const addToInput = (word: string): void => {
        if (word.split(' ').length > 1) {
            if (type === SearchBoxType.Header) {
                storeHeaderSearchQuery(`${headerSearchQuery} "${word}"`);
            } else {
                storeSearchQuery(`${searchQuery} "${word}"`);
            }
            if (type === SearchBoxType.Chart) {
                formik!.setFieldValue('query_string', `${formik!.values.query_string} "${word}"`);
            }
        } else {
            if (type === SearchBoxType.Header) {
                storeHeaderSearchQuery(`${headerSearchQuery} ${word}`);
            } else {
                storeSearchQuery(`${searchQuery} ${word}`);
            }

            if (type === SearchBoxType.Chart) {
                formik!.setFieldValue('query_string', `${formik!.values.query_string} ${word}`);
            }
        }
    };

    const resetParamsSetter = useStoreActions((actions) => actions.search.resetParamsSetter);

    const goToSearchView = () => {
        let dict = {};
        if (type === SearchBoxType.Search) {
            dict = {
                query_string: searchQuery,
            };
        } else if (type === SearchBoxType.Header) {
            dict = {
                query_string: headerSearchQuery,
            };
            storeSearchQuery(headerSearchQuery);
            resetParamsSetter();
        } else {
            dict = {
                query_string: formik!.values.query_string,
            };
        }

        navigate(`${buildSearchDashboardURL()}?${serialize(dict)}`);

        if (type === SearchBoxType.Header) {
            setTimeout(() => {
                storeHeaderSearchQuery('');
                storeHeaderActiveWord(null);
                storeHeaderSimilarWords([]);
                setShow(false);
            }, 100);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<SearchBoxElement>) => {
        if (event.key === 'Enter') {
            goToSearchView();
        }
    };

    let searchQueryMatch: string[] | null;
    if (type === SearchBoxType.Header) {
        searchQueryMatch = headerSearchQuery.match(/[^\s"]+|"([^"]*)"/g);
    } else {
        searchQueryMatch = searchQuery.match(/[^\s"]+|"([^"]*)"/g);
    }
    const arrayOfSearchQuery: string[] = searchQueryMatch ?? [];

    if (arrayOfSearchQuery) {
        for (let i = 0; i < arrayOfSearchQuery.length; i++) {
            arrayOfSearchQuery[i] = arrayOfSearchQuery[i].replace(/['"]+/g, '');
        }
    }


    const SEARCH_QUERY_MAX_LENGTH_CHARACTERS = Number(appConfig.SEARCH_QUERY_MAX_LENGTH_CHARACTERS);

    const syntaxValidation = useMemo(
        () => (type === SearchBoxType.Search ? validateLuceneQuerySyntax(searchQuery) : null),
        [type, searchQuery],
    );
    const hasSyntaxIssues = (syntaxValidation?.issues.length ?? 0) > 0;

    const handleFormatQuery = useCallback(() => {
        if (type !== SearchBoxType.Search) {
            return;
        }

        const formatted = formatLuceneQuery(searchQuery);
        storeSearchQuery(formatted);
        storeActiveWord(null);
        storeErrors(validateQueryString(formatted));
    }, [type, searchQuery, storeSearchQuery, storeActiveWord, storeErrors]);

    const inputChange = (event: React.ChangeEvent<SearchBoxElement>) => {
        if (event.target.value.length > SEARCH_QUERY_MAX_LENGTH_CHARACTERS) {
            setSearchLengthWarning(i18n.t('search_query_exceeds_limit.text', {
                count: SEARCH_QUERY_MAX_LENGTH_CHARACTERS,
            }));
        } else {
            setSearchLengthWarning(null);
            if (type === SearchBoxType.Header) {
                storeHeaderSearchQuery(event.target.value);
                storeHeaderActiveWord(null);
            } else if (type === SearchBoxType.Search) {
                storeSearchQuery(event.target.value);
                storeActiveWord(null);
                storeErrors(validateQueryString(event.target.value));
            } else {
                formik!.setFieldValue('query_string', event.target.value);
            }
        }
    };

    const viewTypeQueryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        storeViewTypeQuery(event.target.value as SearchType);
    };

    const queryHelpLink = (
        <Button
            variant="link"
            type="button"
            onClick={handleOpenModal}
            style={{padding: '0'}}
            className="text-info d-inline-flex align-items-center"
        >
            <Icon name="mdi-open-in-new" size="xs" className="mr-1" />
            {i18n.t('how_to_write_queries.text')}
        </Button>
    );

    let formFeedback;

    if (type === SearchBoxType.Search) {
        formFeedback = queryHelpLink;
    } else if (errors.query_string) {
        formFeedback = (
            <Form.Control.Feedback type="invalid" className="mt-2 d-inline-block">
                <Row>
                    <Col xs={6}>
                        {errors.query_string}
                    </Col>
                    <Col xs={6}>
                        <small className="form-text text-right d-inline-flex align-items-center">
                            {queryHelpLink}
                        </small>
                    </Col>
                </Row>
            </Form.Control.Feedback>
        );
    } else {
        formFeedback = queryHelpLink;
    }

    const searchQueryInvalid = type === SearchBoxType.Search && (hasSyntaxIssues || !!errors.query_string);

    let popoverContent;
    if (loadingCurrentSimilarWords) {
        popoverContent = (<CellLoader fullHeight={false} />);
    } else if (!currentSimilarWords.length) {
        popoverContent = (
            <div className="similar-words-item similar-words-item--error p-1">
                {i18n.t('similar_words_empty.text')}
            </div>
        );
    } else {
        popoverContent = (
            <>
                {currentSimilarWords.map((obj) => {
                    if (arrayOfSearchQuery.includes(obj[0])) return null;
                    if (obj === i18n.t('word2vec_not.text')) {
                        return (
                            <div key={String(obj)} className="similar-words-item similar-words-item--error p-1">
                                {obj}
                            </div>
                        );
                    }
                    return (
                        <div
                            key={obj[0]}
                            className="similar-words-item d-flex align-items-center p-1"
                        >
                            <Button
                                variant="outline-info"
                                size="sm"
                                className="similar-words-add-btn"
                                id={`button-${obj[0]}`}
                                title={i18n.t('es_add_input.text')}
                                onClick={() => addToInput(obj[0])}
                            >
                                <Icon name="mdi-plus" size="xs" />
                            </Button>
                            <span className="similar-words-item-word">{obj[0]}</span>
                        </div>
                    );
                })}
            </>
        );
    }

    return (
        <Form.Group
            ref={currentRef}
            controlId={controlId}
            className={`${type === SearchBoxType.Chart ? 'mb-0' : 'mb-3'} ${className}`}
            style={style}
        >
            {currentActiveWord ? (
                <Overlay
                    target={currentTarget.current}
                    show={show}
                    placement="bottom-start"
                    container={typeof document !== 'undefined' ? document.body : currentRef.current}
                    // TODO why is this trigger prop here? Seems to not do anything at all
                    // @ts-ignore
                    trigger="click"
                    rootClose
                    onHide={(event: Event) => onHide(event)}
                >
                    <Popover id="popover-contained">
                        <Popover.Header as="h3">
                            {`${i18n.t('similar_words.text')} `}
                            {currentActiveWord}
                        </Popover.Header>
                        <Popover.Body style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                        }}
                        >
                            {popoverContent}
                        </Popover.Body>
                    </Popover>
                </Overlay>
            ) : null}
            {type === SearchBoxType.Chart ? (
                <Form.Label className="chart-settings-form-field-label">
                    {i18n.t('jt_search_query.text')}
                </Form.Label>
            ) : null}
            {type === SearchBoxType.Search ? (
                <div className="chart-query-editor search-query-editor">
                    <div className={`chart-query-editor__wrapper${searchQueryInvalid ? ' chart-query-editor__wrapper--invalid' : ''}`}>
                        <TextareaAutosize
                            // @ts-ignore ref shared with header/chart input types
                            ref={currentTarget}
                            className="chart-query-editor__textarea"
                            onClick={(event: React.MouseEvent<SearchBoxElement, MouseEvent>) => onInputClick(event)}
                            placeholder={i18n.t('es_search.text')}
                            autoComplete="off"
                            onChange={(event: React.ChangeEvent<SearchBoxElement>) => inputChange(event)}
                            value={currentSearchQuery}
                            spellCheck={false}
                            minRows={1}
                            data-cy={`${type}-search-input`}
                        />
                        <div className="chart-query-editor__toolbar">
                            <span className="chart-query-editor__char-count">
                                {currentSearchQuery.length} chars
                            </span>
                            <button
                                type="button"
                                className="chart-query-editor__action-btn"
                                onClick={handleFormatQuery}
                            >
                                {i18n.t('cs_format_query.text')}
                            </button>
                        </div>
                    </div>
                    {hasSyntaxIssues ? (
                        <ChartQuerySyntaxFeedback query={currentSearchQuery} />
                    ) : null}
                </div>
            ) : (
                <Form.Control
                    as={type === SearchBoxType.Header ? 'input' : TextareaAutosize}
                    // TS doesn't know that this can also be non-input element
                    // @ts-ignore
                    ref={currentTarget}
                    onClick={(event: React.MouseEvent<SearchBoxElement, MouseEvent>) => onInputClick(event)}
                    async
                    placeholder={i18n.t('es_search.text')}
                    autoComplete="off"
                    onChange={(event: React.ChangeEvent<SearchBoxElement>) => inputChange(event)}
                    value={currentSearchQuery}
                    isInvalid={!!currentErrors}
                    onKeyPress={type === SearchBoxType.Header ? handleKeyPress : undefined}
                    data-cy={`${type}-search-input`}
                />
            )}
            {searchLengthWarning && (
                <div className="text-danger mt-2">
                    {searchLengthWarning}
                </div>
            )}
            {type === SearchBoxType.Chart ? (
                <Form.Control.Feedback type="invalid">
                    {typeof formik!.errors.query_string === 'string' ? formik!.errors.query_string : ''}
                </Form.Control.Feedback>
            ) : null}

            {type === SearchBoxType.Chart && !deferHelpLink ? (
                <ChartSearchQueryHelpLink />
            ) : null}

            {type !== SearchBoxType.Chart ? (
                <a
                    onClick={() => {
                        goToSearchView();
                    }}
                    data-cy="header-search-btn"
                >
                    <Icon name="mdi-magnify" className="text-info input-icon cursor-pointer" />
                </a>
            ) : null}

            {type === SearchBoxType.Search ? (
                <div className="d-flex align-items-center justify-content-end mt-2">
                    <span className="mr-2">{formFeedback}</span>
                    <Tooltip content={i18n.t('jt_settings.text')} placement="top">
                        <Dropdown
                            className="d-inline-flex align-items-center"
                            drop="end"
                        >
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-cog"
                                iconSize="18px"
                                className="dropdown-toggle-no-caret"
                                id="search-settings-dropdown"
                                data-cy="search-box-outlineinfo-btn-2"
                            />

                            <Dropdown.Menu>
                                <Dropdown.ItemText style={{
                                    width: '250px',
                                }}
                                >
                                    <Form.Group className="mb-3" >
                                        <Form.Label>
                                            {i18n.t('jt_view_type.text')}
                                        </Form.Label>
                                        {
                                            userCanAccessViewTypeFieldChoices ? (
                                                <Form.Control
                                                    as="select"
                                                    name="viewType"
                                                    value={viewTypeQuery}
                                                    onChange={
                                                        (event: React.ChangeEvent<any>) => viewTypeQueryChange(event)
                                                    }
                                                >
                                                    {viewTypeFieldChoices.map((el) => (
                                                        <option key={el[0]} value={el[0]}>
                                                            {viewTypeI18nKeyByValue[el[0]] ? i18n.t(viewTypeI18nKeyByValue[el[0]]) : el[1]}
                                                        </option>
                                                    ))}
                                                </Form.Control>
                                            ) : (
                                                <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement="top"
                                                >
                                                    <Form.Control
                                                        as="select"
                                                        name="sortType"
                                                        disabled
                                                    />
                                                </Tooltip>
                                            )
                                        }
                                    </Form.Group>
                                </Dropdown.ItemText>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ) : null}
        </Form.Group>
    );
}

/**
 * Wrapper component that loads userData/config via useOptimizedConfig and then
 * renders the inner SearchBox once data is available. This separation helps
 * keep hook order stable inside SearchBoxInner.
 */
function SearchBox(props: SearchBoxProps) {
    // NOTE: Import kept local to avoid circular deps in some build setups
    // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
    const {default: useOptimizedConfig} = require('@/hooks/useOptimizedConfig') as typeof import('@/hooks/useOptimizedConfig');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    if (loadingUserData || !userData) {
        return <CellLoader fullHeight={true} text={i18n.t('loading_search.text')} />;
    }

    return <SearchBoxInner {...props} userData={userData} />;
}

export default React.memo(SearchBox);
